'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import Dialog from '@enlighten/design-system/dist/Dialog';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import type { NoticePopupValue } from './types';

interface Props {
	popup: NoticePopupValue;
}

const DISMISSED_ID_KEY = 'noticePopupDismissedId';

const NoticePopup = ({ popup }: Props) => {
	const [isClosed, setIsClosed] = useState(false);
	const [dismissedId, setDismissedId] = useState<string | null>(null);

	const [isMounted, setIsMounted] = useState(false);

	const open = isMounted && !isClosed && dismissedId !== `${popup.id}`;

	const close = () => {
		setIsClosed(true);
	};

	const closeForever = () => {
		try {
			localStorage.setItem(DISMISSED_ID_KEY, `${popup.id}`);
		} catch {
			// 저장소 접근 불가 환경(시크릿 모드 등)에서는 무시
		}

		setIsClosed(true);
	};

	useEffect(() => {
		try {
			setDismissedId(localStorage.getItem(DISMISSED_ID_KEY));
		} catch {
			//
		}

		setIsMounted(true);
	}, []);

	if (!open) {
		return null;
	}

	const { image, url } = popup;
	const isExternalUrl = url && url.startsWith('http');
	/** WP 본문에 `notice` 처럼 슬래시 없이 적히기도 해서 내부 경로는 앞에 붙여준다 */
	const href = url && (isExternalUrl || url.startsWith('/') ? url : `/${url}`);

	return (
		<Dialog
			open
			/** 기본값(theme.zIndex.modal = 1300)은 젠데스크 위젯보다 낮아 가려진다 */
			sx={{ zIndex: 9999999 }}
			slotProps={{
				paper: {
					sx: { width: '100%', maxWidth: '614px', padding: 0, backgroundColor: 'transparent' },
				},
			}}
			onClose={(_event, reason) => {
				if (reason !== 'backdropClick') {
					close();
				}
			}}
		>
			<Box
				component={href ? Link : 'div'}
				href={href}
				target={isExternalUrl ? '_blank' : undefined}
				rel={isExternalUrl ? 'noopener noreferrer' : undefined}
				aria-label={image.alt}
				position="relative"
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					'& img': {
						display: 'block',
						width: '100%',
						height: 'auto',
					},
				}}
			>
				<Image src={image.url} alt="" width={image.width} height={image.height} />
			</Box>

			<Box display="flex" alignItems="center" justifyContent="space-between" height="56px" flexShrink={0} paddingX={2} bgcolor="grey.900">
				<Button variant="text" sx={{ padding: 0, height: 'inherit', color: 'common.white' }} onClick={closeForever}>
					다시 보지 않기
				</Button>
				<Button variant="text" sx={{ padding: 0, minWidth: 0, height: 'inherit', color: 'common.white' }} onClick={close}>
					닫기
				</Button>
			</Box>
		</Dialog>
	);
};

export default NoticePopup;
